import type { SupplierChangeReason } from '@utazzo/shared';
import type { UzSelectOption } from '@utazzo/ui';

/**
 * Draft-state row shapes and small conversion helpers shared across the
 * create-invoice form sections. The page owns the state; the section
 * components are presentational and receive slices + setters.
 *
 * Money is held as rupee numbers (or `''` while empty) and only converted to
 * BigInt-safe paise STRINGS at submit time. Dates are held as `Date | null`
 * and converted to ISO `yyyy-mm-dd` strings at submit time.
 */

export type ProductType = 'DOM' | 'INT';
export type GstSlab = 'PKG' | 'CRP' | 'B2C' | 'B2CF';
export type PaxType = 'ADT' | 'CHD' | 'INF';
/** Legacy fare/journey type: 1 One Way, 2 Return, 3 MultiCity. */
export type FareType = 1 | 2 | 3;

export interface PassengerRow {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  paxType: PaxType;
  dob: Date | null;
  panNo: string;
  // INT-only
  passportNumber: string;
  expiryDate: Date | null;
  placeOfIssue: string;
  nationalityCountryId: string;
}

export interface MulticityRow {
  departureCity: string;
  destinationCity: string;
  departureDate: Date | null;
}

export interface AirlineSectorRow {
  paxName: string;
  eTicket: string;
  pnr: string;
  airlineId: string;
  source: string;
  flightType: string;
  carrierType: string;
}

export interface TrainBusRow {
  pnr: string;
  bookingClass: string;
  quota: string;
}

export interface DepositRow {
  amount: number | '';
  balanceDueDate: Date | null;
  isPaid: boolean;
}

export interface SupplierRow {
  /** Existing supplier-line id (edit mode only). */
  id?: string;
  /** Live (non-voided) payments on this line (edit mode only). */
  livePaymentCount?: number;
  /** Supplier the line was loaded with, to detect a real change (edit mode only). */
  originalSupplierId?: string;
  /** Why the supplier changed on a line that has payments. */
  supplierChangeReason?: SupplierChangeReason;
  supplierId: string;
  description: string;
  paymentDueDate: Date | null;
  payable: number | '';
  cgst: number | '';
  sgst: number | '';
  igst: number | '';
  tds: number | '';
  currencyId: string;
  exchangeRate: number | '';
  noOfCurrency: number | '';
}

export const emptyPassengerRow = (): PassengerRow => ({
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  paxType: 'ADT',
  dob: null,
  panNo: '',
  passportNumber: '',
  expiryDate: null,
  placeOfIssue: '',
  nationalityCountryId: '',
});

export const emptyMulticityRow = (): MulticityRow => ({
  departureCity: '',
  destinationCity: '',
  departureDate: null,
});

export const emptyAirlineSectorRow = (): AirlineSectorRow => ({
  paxName: '',
  eTicket: '',
  pnr: '',
  airlineId: '',
  source: '',
  flightType: '',
  carrierType: '',
});

export const emptyTrainBusRow = (): TrainBusRow => ({ pnr: '', bookingClass: '', quota: '' });

export const emptyDepositRow = (): DepositRow => ({
  amount: '',
  balanceDueDate: null,
  isPaid: false,
});

export const emptySupplierRow = (): SupplierRow => ({
  supplierId: '',
  description: '',
  paymentDueDate: null,
  payable: '',
  cgst: '',
  sgst: '',
  igst: '',
  tds: '',
  currencyId: '',
  exchangeRate: '',
  noOfCurrency: '',
});

/** Rupees (or blank) to whole paise (number), rounded. */
export const rupeesToPaiseNum = (rupees: number | ''): number =>
  rupees === '' ? 0 : Math.round(rupees * 100);

/** Rupees (or blank) to a paise STRING (rupees*100), BigInt-safe. */
export const rupeesToPaise = (rupees: number | ''): string => String(rupeesToPaiseNum(rupees));

/** `Date` to an ISO `yyyy-mm-dd` string (local), or undefined when null. */
export const toIsoDate = (d: Date | null): string | undefined => {
  if (!d || Number.isNaN(d.getTime())) return undefined;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Paise STRING (BigInt-safe) to a rupee number for the form inputs; blank
 * (`''`) when the amount is zero so empty money fields stay visually empty. The
 * inverse of `rupeesToPaise` used when hydrating the form in edit mode.
 */
export const paiseToRupees = (paise: string | null | undefined): number | '' => {
  if (paise == null) return '';
  const n = Number(paise);
  return Number.isFinite(n) && n !== 0 ? n / 100 : '';
};

/** ISO date string to a `Date` (or null when absent / invalid) for the pickers. */
export const isoToDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Gross of a supplier row (paise): payable - tds. */
export const supplierRowGrossPaise = (row: SupplierRow): number =>
  rupeesToPaiseNum(row.payable) - rupeesToPaiseNum(row.tds);

/** Slabs that expose the With/Without service-charge Bill Type control. */
export const slabUsesBillType = (slab: GstSlab | ''): boolean => slab === 'B2C' || slab === 'B2CF';

export const PRODUCT_TYPE_OPTIONS: ReadonlyArray<UzSelectOption<ProductType>> = [
  { value: 'DOM', label: 'DOM - Domestic' },
  { value: 'INT', label: 'INT - International' },
];

export const BILL_TYPE_OPTIONS: ReadonlyArray<UzSelectOption<string>> = [
  { value: '1', label: 'With Service Charge' },
  { value: '2', label: 'Without Service Charge' },
];
